/**
 * Join Team page
 * Allows users to join a team via invite code
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { joinTeam } from '../../api/teams';
import { useTeamStore } from '../../stores/teamStore';
import { apiErrorMessage } from '../../utils/apiError';

export default function JoinTeamPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { loadTeams } = useTeamStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!code) {
      setError('Código de convite inválido');
      setIsLoading(false);
      return;
    }

    const handleJoin = async () => {
      setIsLoading(true);
      setError(null);
      try {
        await joinTeam(code);
        setSuccess(true);
        await loadTeams();

        // Redirect to teams page after 2 seconds
        setTimeout(() => {
          navigate('/teams', { replace: true });
        }, 2000);
      } catch (err) {
        setError(apiErrorMessage(err, 'Falha ao entrar na equipe'));
      } finally {
        setIsLoading(false);
      }
    };

    handleJoin();
  }, [code, loadTeams, navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />
      <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="max-w-md w-full mx-4">
          {isLoading && (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C8A96E]/10 mx-auto mb-6">
                <div className="w-10 h-10 border-4 border-[#C8A96E]/20 border-t-[#C8A96E] rounded-full animate-spin"></div>
              </div>
              <h1 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Entrando na equipe...
              </h1>
              <p className="text-white/40 text-sm">Aguarde um momento</p>
            </div>
          )}

          {success && (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#7EB8A4]/10 mx-auto mb-6">
                <i className="ri-check-line text-3xl text-[#7EB8A4]"></i>
              </div>
              <h1 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Bem-vindo!
              </h1>
              <p className="text-white/60 text-sm mb-4">Você entrou na equipe com sucesso</p>
              <p className="text-white/30 text-xs">Redirecionando em 2 segundos...</p>
            </div>
          )}

          {error && (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-500/10 mx-auto mb-6">
                <i className="ri-error-warning-line text-3xl text-red-400"></i>
              </div>
              <h1 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Erro
              </h1>
              <p className="text-white/60 text-sm mb-6">{error}</p>
              <button
                onClick={() => navigate('/teams', { replace: true })}
                className="w-full px-4 py-3 bg-[#C8A96E] text-[#0A0A0F] text-sm font-semibold rounded-lg hover:bg-[#D4B87A] transition-colors"
              >
                ← Voltar para Equipes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
